/**
 * Record link clicks once, for every link, without touching any link.
 *
 * The site could only see a click if the href pointed at /go/<asset>, so every
 * new download meant remembering to route it through the redirect, and every
 * other outbound link -- Play Store, GitHub, the social icons, the docs -- was
 * invisible. Wiring an onClick per anchor is the same maintenance burden moved
 * into JSX, and it silently misses any link added later.
 *
 * So this is a single delegated listener installed once. It sits in the capture
 * phase on document, sees every click on the page including ones inside
 * components that stopPropagation, and needs nothing from the markup. A link
 * added tomorrow is measured tomorrow, with no change here or at the call site.
 *
 * It reports through the server's existing attribution log rather than a second
 * pipeline: the beacon hits /collect, which calls the same logAttribution() that
 * /go/ and page views already use, so every event lands in one file with one
 * schema.
 *
 * PRIVACY, matching what the server already does:
 *   - Outbound and download clicks are always recorded. They are conversions,
 *     and they are the thing the funnel is for.
 *   - Internal navigation is recorded ONLY for tagged traffic, the same rule
 *     logAttribution applies to page views. Logging every in-site click of
 *     every visitor would turn a funnel into surveillance.
 *   - The href is recorded. The visitor is not: no IP, no user agent, no
 *     cookie, no fingerprint. That is the server's stance and this does not
 *     widen it.
 */

const ENDPOINT = '/collect';

/** Tagged traffic carries these from a campaign link. */
function isTagged(search) {
  const q = new URLSearchParams(search || '');
  return Boolean(q.get('ref') || q.get('c') || q.get('t'));
}

/** download | outbound | internal — what kind of click this was. */
export function classifyLink(anchor, currentOrigin) {
  const raw = anchor.getAttribute('href') || '';
  if (!raw || raw.startsWith('#')) return null;
  // mailto:, tel:, and anything else non-http is a real intent worth counting.
  if (/^(mailto|tel):/i.test(raw)) return 'contact';
  if (raw.startsWith('/go/')) return 'download';

  let url;
  try {
    url = new URL(raw, currentOrigin);
  } catch (e) {
    return null;
  }
  if (!/^https?:$/.test(url.protocol)) return null;
  return url.origin === currentOrigin ? 'internal' : 'outbound';
}

/** Whether a click of this kind should be recorded at all. */
export function shouldRecord(kind, search) {
  if (kind === null) return false;
  if (kind === 'internal') return isTagged(search);
  return true;
}

export function installLinkAttribution(doc = document, win = window) {
  if (!doc || !doc.addEventListener || doc.__linkAttributionInstalled) return () => {};
  doc.__linkAttributionInstalled = true;

  const onClick = (event) => {
    try {
      // composedPath so a click on an <img> or <span> inside the anchor still
      // finds it, and so it works through shadow roots.
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      const anchor = path.find((n) => n && n.tagName === 'A')
        || (event.target && event.target.closest && event.target.closest('a'));
      if (!anchor) return;

      const kind = classifyLink(anchor, win.location.origin);
      if (!shouldRecord(kind, win.location.search)) return;

      const body = JSON.stringify({
        event: 'link_click',
        kind,
        href: anchor.getAttribute('href'),
        // Where the click happened, so a download link in the footer is
        // distinguishable from the same link in the hero.
        from: win.location.pathname,
        text: (anchor.textContent || '').trim().slice(0, 80),
      });

      // sendBeacon survives the navigation that this click is about to cause.
      // fetch() would be cancelled when the page unloads, which is precisely
      // the case worth measuring.
      if (win.navigator && typeof win.navigator.sendBeacon === 'function') {
        win.navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
      }
    } catch (e) {
      // Measurement must never break a link. A lost row is acceptable; a click
      // that does not navigate is not.
    }
  };

  doc.addEventListener('click', onClick, true);
  return () => {
    doc.removeEventListener('click', onClick, true);
    doc.__linkAttributionInstalled = false;
  };
}

export default installLinkAttribution;
